import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RowLayout } from '../../Vendors/VendorCreation/General/VendorForm/RowLayout'
import { FieldWithLabel } from '../../../../components/FieldWithLabel/FieldWithLabel'
import { Typography } from '../../../../components/Typography'
import { getVendorInfo } from '../../../../redux/store/vendorInfo/getters'
import {
  formatAddress,
  fullFormatPhoneNumber,
  safeString
} from '../../../../helper/common'
import { fetchVendorOptions } from '../../../PaymentDetails/api'
import { IconFullName } from 'components/IconFullName/IconFullName'
import { onStartChat } from 'features/MyCommunity/utils'
import {
  LABELS,
  ROLES_NAMES,
  TAddress,
  TOptionsResponse
} from '../../../../constants'

export const General = () => {
  const vendorInfo = useSelector(getVendorInfo)
  const [
    federalTaxClassificationTypeList,
    setFederalTaxClassificationTypeList
  ] = useState<TOptionsResponse[]>([])

  const getFederalTaxClassificationTypeList = async () => {
    await fetchVendorOptions().then((res) => {
      setFederalTaxClassificationTypeList(res.data.results)
    })
  }

  const contactPerson = useMemo(() => {
    return vendorInfo?.contract_admin?.uuid
      ? vendorInfo?.contract_admin
      : vendorInfo?.contract_steward
  }, [vendorInfo])

  useEffect(() => {
    getFederalTaxClassificationTypeList()
    setFederalTaxClassificationTypeList([])
  }, [])

  return (
    <div className="general-details">
      <RowLayout title="Access type">
        <FieldWithLabel title={LABELS.VENDOR_PERMISSION}>
          {vendorInfo.vendor_permission === 'guest' ? (
            <Typography.Body1>
              Guest{' '}
              <span>(access only for Product Requests functionality)</span>
            </Typography.Body1>
          ) : (
            <Typography.Body1>Full access</Typography.Body1>
          )}
        </FieldWithLabel>
      </RowLayout>
      <div className="general-details__divider" />
      <RowLayout title="Business information">
        <div className="row space-between">
          <FieldWithLabel
            title="Vendor name (doing business as)"
            className="prs-details-general__left__contract-category"
          >
            {vendorInfo.business_name}
          </FieldWithLabel>
          <FieldWithLabel
            title={LABELS.LEGAL_NAME}
            className="prs-details-general__left__contract-category"
          >
            {vendorInfo.name}
          </FieldWithLabel>
        </div>
        <FieldWithLabel title="Vendor alternate name(s)">
          {!!vendorInfo.alternate_name_set?.length
            ? vendorInfo.alternate_name_set.map((i) => i.name)?.join(', ')
            : '-'}
        </FieldWithLabel>
        <div className="row space-between">
          <FieldWithLabel
            title={LABELS.BUSINESS_TYPE}
            className="prs-details-general__left__contract-category"
          >
            {federalTaxClassificationTypeList?.find(
              (i) => i.id === vendorInfo?.business_type
            )?.value || '-'}
          </FieldWithLabel>
        </div>
        <FieldWithLabel title="Vendor’s phone number">
          {fullFormatPhoneNumber(vendorInfo?.phone_number)}
        </FieldWithLabel>
        <FieldWithLabel title={LABELS.DESCRIPTION}>
          {safeString(vendorInfo?.description)}
        </FieldWithLabel>
      </RowLayout>
      <div className="general-details__divider" />
      <RowLayout title="Addresses">
        <FieldWithLabel title="Legal address">
          <Typography.Body1 className="white-space-pre-line">
            {formatAddress(vendorInfo?.address as TAddress) || '-'}
          </Typography.Body1>
        </FieldWithLabel>
        {vendorInfo?.order_from_address_set?.map((item, idx) => (
          <FieldWithLabel
            title={`Order from address ${idx + 1}`}
            key={`Order from address ${idx + 1}`}
          >
            <Typography.Body1 className="white-space-pre-line">
              {formatAddress(item)}
            </Typography.Body1>
          </FieldWithLabel>
        ))}
        {vendorInfo?.remit_to_address_set?.map((item, idx) => (
          <FieldWithLabel
            title={`Remit to address ${idx + 1}`}
            key={`Remit to address ${idx + 1}`}
          >
            <Typography.Body1 className="white-space-pre-line">
              {formatAddress(item)}
            </Typography.Body1>
          </FieldWithLabel>
        ))}
      </RowLayout>
      <div className="general-details__divider" />
      {(vendorInfo?.contract_admin || vendorInfo?.contract_steward) && (
        <RowLayout
          title={
            vendorInfo.vendor_permission === 'guest'
              ? 'Contract Steward'
              : 'Contract admin'
          }
        >
          <IconFullName
            className="column"
            firstName={contactPerson?.first_name}
            lastName={contactPerson?.last_name}
            roleName={
              contactPerson?.role ? ROLES_NAMES[contactPerson?.role] : ''
            }
            phone={fullFormatPhoneNumber(contactPerson?.phone_number || '')}
            email={contactPerson?.email}
            src={contactPerson?.avatar?.file_preview}
            uuid={contactPerson?.uuid}
            onStartChat={onStartChat}
          />
        </RowLayout>
      )}
    </div>
  )
}
